import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    Typography,
    Skeleton,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from "recharts";
import { useEffect, useState } from "react";
import { getRequest } from "../../globalhelper/helper";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6"];

const VendorDashboard = ({ open, setOpen, compact = false, company, globalFromDate, globalToDate }) => {
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        if (company) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    // Prioritize local state only if dialog is open
                    const effectiveFromDate = open ? fromDate : globalFromDate;
                    const effectiveToDate = open ? toDate : globalToDate;

                    const formattedFromDate = effectiveFromDate ? effectiveFromDate.format("DD-MM-YYYY") : "";
                    const formattedToDate = effectiveToDate ? effectiveToDate.format("DD-MM-YYYY") : "";

                    let { res } = await getRequest(`api/dashboard/getTopVendors?orgid=${company.orgid}&fromDate=${formattedFromDate}&toDate=${formattedToDate}`);
                    let parsed = JSON.parse(res.data || "[]");

                    if (isMounted && parsed.length) {
                        const coloredData = parsed.map((item, index) => {
                            const val = parseFloat(item.totalgoodsreceipt) || 0;
                            let display = val;
                            if (val >= 10000000) display = (val / 10000000).toFixed(2) + "Cr";
                            else if (val >= 100000) display = (val / 100000).toFixed(2) + "L";
                            else display = val.toLocaleString();
                            return {
                                ...item,
                                totalgoodsreceipt: val,
                                displayValue: display,
                                color: COLORS[index % COLORS.length],
                            };
                        });
                        setData(coloredData);
                    } else {
                        setData([]);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    if (isMounted) setLoading(false);
                }
            };
            fetchData();
        }
        return () => { isMounted = false; };
    }, [company, fromDate, toDate, globalFromDate, globalToDate, open]);

    /* ─── FULL BAR CHART MODE ─── */
    return (
        <Card sx={{
            height: open ? "85vh" : 460,
            minHeight: open ? "85vh" : 460,
            borderRadius: 4,
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            border: "1px solid #f1f5f9",
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.08)" },
        }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box sx={{ p: 1, bgcolor: "#f0fdf4", borderRadius: "12px", display: "flex" }}>
                            <TrendingUpIcon sx={{ color: "#10b981", fontSize: "1.25rem" }} />
                        </Box>
                        Top Contributors
                    </Typography>
                    {!open && <IconButton onClick={() => setOpen((prev) => ({ ...prev, vendor: !prev.vendor }))} sx={{ bgcolor: "#f8fafc" }}><OpenInFullIcon sx={{ fontSize: "1.1rem" }} /></IconButton>}
                </Box>

                {open && (
                    <Grid container spacing={1.5} mb={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="From"
                                    value={fromDate}
                                    inputFormat="DD-MM-YYYY"
                                    onChange={(newValue) => setFromDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            {...params}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="To"
                                    value={toDate}
                                    inputFormat="DD-MM-YYYY"
                                    onChange={(newValue) => setToDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            {...params}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                )}
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {loading ? (
                        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: 2.5, px: 2, pt: 1 }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Skeleton width={130} height={20} />
                                    <Skeleton variant="rounded" width="50%" height={24} sx={{ borderRadius: 1 }} />
                                    <Skeleton width={50} height={18} />
                                </Box>
                            ))}
                        </Box>
                    ) : data.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                layout="vertical"
                                data={data}
                                margin={{ top: 5, right: 80, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                                <XAxis type="number" hide />
                                <YAxis
                                    dataKey="vendorname"
                                    type="category"
                                    tick={{ fontSize: open ? 14 : 11, fill: "#475569", fontWeight: 700 }}
                                    width={130}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => v.length > 18 && !open ? v.slice(0, 18) + "…" : v}
                                />
                                <Tooltip
                                    cursor={{ fill: "transparent" }}
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Box sx={{
                                                    bgcolor: "rgba(255,255,255,0.96)",
                                                    p: 1.2,
                                                    border: "1px solid #f1f5f9",
                                                    borderRadius: 2,
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                                                }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: "#475569", fontSize: "0.8rem" }}>
                                                        {payload[0].payload.vendorname}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: payload[0].payload.color, fontWeight: 700, fontSize: "0.75rem" }}>
                                                        {payload[0].payload.displayValue}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Bar dataKey="totalgoodsreceipt" radius={[0, 4, 4, 0]} barSize={22}>
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.7} />
                                    ))}
                                </Bar>
                                <Bar
                                    dataKey="totalgoodsreceipt"
                                    fill="transparent"
                                    label={({ x, y, width, index }) => (
                                        <text x={x + width + 8} y={y + 15} fill="#475569" fontSize={12} fontWeight={700} textAnchor="start">
                                            {data[index]?.displayValue}
                                        </text>
                                    )}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <Box sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            gap: 1,
                            fill: "#0f172a",
                            fontWeight: 800,
                        }}>
                            <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                No data found
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent >
        </Card >
    );
};

export default VendorDashboard;
